import * as stylex from '@stylexjs/stylex'
import { type MouseEvent, type ReactNode } from 'react'
import { ButtonBase, type ButtonBaseProps } from '../ButtonBase/ButtonBase'
import { type TabColor, type TabValue } from '../TabContext/TabContext'
import { colors } from '../tokens/colors.stylex'

const sizeVariants = stylex.create({
  small: {
    padding: '8px 12px',
  },
  medium: {
    padding: '12px 12px',
  },
})

const colorVariants = stylex.create({
  primary: {
    color: colors.primary600,
  },
  blue: {
    color: colors.blue600,
  },
  grey: {
    color: colors.grey600,
  },
  red: {
    color: colors.red600,
  },
})

const baseStyles = stylex.create({
  root: {
    padding: '12px',
    overflow: 'hidden',
    color: colors.grey500,
    flexShrink: 0,
    lineHeight: 1.25,
    position: 'relative',
    textAlign: 'center',
    whiteSpace: 'normal',
    maxWidth: 360,
  },
  disabled: {
    color: colors.grey400,
  },
  fullWidth: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    maxWidth: 'none',
  },
  divider: {
    borderColor: colors.grey300,
    borderStyle: 'solid',
    borderBottomWidth: 1,
  },
})

export interface TabProps<Value extends TabValue = TabValue> extends Omit<ButtonBaseProps, 'onChange'> {
  /** 標籤頁切換時的回呼 */
  onChange?: (event: MouseEvent<HTMLButtonElement>, value?: Value) => void
  /** 是否為選取狀態（由 TabList 自動注入） */
  selected?: boolean
  /** 標籤頁對應值 */
  value?: Value
  /** 選取指示器（由 TabList 自動注入） */
  indicator?: ReactNode
  /** 是否撐滿可用寬度（由 TabList variant='fullWidth' 注入） */
  fullWidth?: boolean
  /**
   * 選取時的顏色
   * @default 'primary'
   */
  color?: TabColor
  /**
   * 尺寸
   * @default 'medium'
   */
  size?: 'small' | 'medium'
  /**
   * 是否顯示底部分隔線，僅適用於 horizontal 排列
   * @default false
   */
  divider?: boolean
}

/**
 * Tab 單一標籤頁按鈕，需搭配 TabList / TabContext 使用。
 * 支援 small / medium 尺寸及多種主題色。
 */
export const Tab = (props: TabProps) => {
  const {
    rootStyle,
    indicator,
    onChange,
    onClick,
    selected,
    disabled,
    value,
    children,
    fullWidth,
    color = 'primary',
    size = 'medium',
    divider = false,
    ...rest
  } = props

  const handleClick: ButtonBaseProps['onClick'] = (event) => {
    if (!selected && onChange) {
      onChange(event, value)
    }

    if (onClick) {
      onClick(event)
    }
  }

  return (
    <ButtonBase
      role="tab"
      tabIndex={selected ? 0 : -1}
      rootStyle={[
        rootStyle,
        baseStyles.root,
        selected && colorVariants[color],
        disabled && baseStyles.disabled,
        fullWidth && baseStyles.fullWidth,
        sizeVariants[size],
        divider && baseStyles.divider,
      ]}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      {children}
      {indicator}
    </ButtonBase>
  )
}
